import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Settings,
  Plus,
  Trash2,
  Save,
  Loader2,
  GripVertical,
  Upload,
  Download,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-provider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PipelineStage {
  id?: string;
  name: string;
  order: number;
  color: string;
}

interface Pipeline {
  _id: string;
  name: string;
  stages: PipelineStage[];
  isDefault: boolean;
  createdAt: string;
}

interface PipelineFormState {
  _id?: string; // undefined for new
  name: string;
  stages: PipelineStage[];
  isDefault: boolean;
  isNew?: boolean;
}

interface ImportResult {
  created: number;
  skipped: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_STAGES: PipelineStage[] = [
  { name: 'New Lead', order: 0, color: '#6366f1' },
  { name: 'Qualified', order: 1, color: '#8b5cf6' },
  { name: 'Proposal', order: 2, color: '#f59e0b' },
  { name: 'Won', order: 3, color: '#10b981' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CRMSettings() {
  const { projectId } = useParams<{ projectId: string }>();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const basePath = `/projects/${projectId}/crm`;

  // Pipeline editing state: array of forms (existing + unsaved new)
  const [pipelineForms, setPipelineForms] = useState<PipelineFormState[]>([]);
  const [formsInitialized, setFormsInitialized] = useState(false);

  // Import / Export state
  const [importJson, setImportJson] = useState('');
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Track which pipelines are currently saving / deleting
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  const { data: pipelinesData, isLoading } = useQuery({
    queryKey: ['crm-pipelines', projectId],
    queryFn: async () => {
      const res = await api.get(`${basePath}/pipelines`);
      return res.data as { success: boolean; data: Pipeline[] };
    },
    enabled: Boolean(projectId),
  });

  const pipelines = pipelinesData?.data ?? [];

  // Sync server pipelines into local form state once
  if (pipelines.length > 0 && !formsInitialized) {
    setPipelineForms(
      pipelines.map((p) => ({
        _id: p._id,
        name: p.name,
        stages: p.stages.slice().sort((a, b) => a.order - b.order),
        isDefault: p.isDefault,
      }))
    );
    setFormsInitialized(true);
  }

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const createPipeline = useMutation({
    mutationFn: async (data: { name: string; stages: PipelineStage[]; isDefault?: boolean }) => {
      const res = await api.post(`${basePath}/pipelines`, data);
      return res.data as { success: boolean; data: Pipeline };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines', projectId] });
      setFormsInitialized(false);
      toast.success('Pipeline created');
    },
    onError: () => toast.error('Failed to create pipeline'),
  });

  const updatePipeline = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { name: string; stages: PipelineStage[]; isDefault?: boolean };
    }) => {
      const res = await api.put(`${basePath}/pipelines/${id}`, data);
      return res.data as { success: boolean; data: Pipeline };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines', projectId] });
      setFormsInitialized(false);
      toast.success('Pipeline updated');
    },
    onError: () => toast.error('Failed to update pipeline'),
  });

  const deletePipeline = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`${basePath}/pipelines/${id}`);
      return res.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines', projectId] });
      setPipelineForms((prev) => prev.filter((f) => f._id !== id));
      setFormsInitialized(false);
      toast.success('Pipeline deleted');
    },
    onError: (error: any) => {
      const code = error?.response?.data?.error?.code;
      if (code === 'HAS_DEALS') {
        toast.error('Cannot delete pipeline with existing deals. Move or delete deals first.');
      } else {
        toast.error('Failed to delete pipeline');
      }
    },
  });

  const importContacts = useMutation({
    mutationFn: async (data: { contacts: unknown[]; skipDuplicates: boolean }) => {
      const res = await api.post(`${basePath}/import`, data);
      return res.data as { success: boolean; data: ImportResult };
    },
    onSuccess: (res) => {
      setImportResult(res.data);
      toast.success('Import complete');
    },
    onError: () => toast.error('Import failed. Check your JSON format.'),
  });

  // -------------------------------------------------------------------------
  // Pipeline form helpers
  // -------------------------------------------------------------------------

  function updateForm(index: number, updates: Partial<PipelineFormState>) {
    setPipelineForms((prev) => prev.map((f, i) => (i === index ? { ...f, ...updates } : f)));
  }

  function updateStage(pipelineIdx: number, stageIdx: number, updates: Partial<PipelineStage>) {
    setPipelineForms((prev) =>
      prev.map((f, i) => {
        if (i !== pipelineIdx) return f;
        const stages = f.stages.map((s, si) => (si === stageIdx ? { ...s, ...updates } : s));
        return { ...f, stages };
      })
    );
  }

  function addStage(pipelineIdx: number) {
    setPipelineForms((prev) =>
      prev.map((f, i) => {
        if (i !== pipelineIdx) return f;
        return {
          ...f,
          stages: [...f.stages, { name: 'New Stage', order: f.stages.length, color: '#6366f1' }],
        };
      })
    );
  }

  function removeStage(pipelineIdx: number, stageIdx: number) {
    setPipelineForms((prev) =>
      prev.map((f, i) => {
        if (i !== pipelineIdx) return f;
        const stages = f.stages
          .filter((_, si) => si !== stageIdx)
          .map((s, idx) => ({ ...s, order: idx }));
        return { ...f, stages };
      })
    );
  }

  function addNewPipeline() {
    const tempId = `new-${Date.now()}`;
    setPipelineForms((prev) => [
      ...prev,
      {
        _id: tempId,
        name: 'New Pipeline',
        stages: DEFAULT_STAGES.map((s) => ({ ...s })),
        isDefault: false,
        isNew: true,
      },
    ]);
  }

  function removeNewPipeline(index: number) {
    setPipelineForms((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSavePipeline(form: PipelineFormState, _index: number) {
    if (!form.name.trim()) {
      toast.error('Pipeline name is required');
      return;
    }
    if (form.stages.length < 2) {
      toast.error('Pipeline must have at least 2 stages');
      return;
    }
    for (const s of form.stages) {
      if (!s.name.trim()) {
        toast.error('All stages must have a name');
        return;
      }
    }

    const id = form._id!;
    setSavingIds((prev) => new Set(prev).add(id));

    const payload = {
      name: form.name.trim(),
      stages: form.stages.map((s, idx) => ({
        ...(s.id ? { id: s.id } : {}),
        name: s.name.trim(),
        order: idx,
        color: s.color,
      })),
      isDefault: form.isDefault,
    };

    try {
      if (form.isNew) {
        await createPipeline.mutateAsync(payload);
      } else {
        await updatePipeline.mutateAsync({ id, data: payload });
      }
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  async function handleDeletePipeline(form: PipelineFormState, index: number) {
    if (form.isNew) {
      removeNewPipeline(index);
      return;
    }
    if (
      !(await confirm({
        title: 'Delete pipeline?',
        message: 'Are you sure you want to delete this pipeline?',
        danger: true,
      }))
    ) {
      return;
    }

    const id = form._id!;
    setDeletingIds((prev) => new Set(prev).add(id));
    try {
      await deletePipeline.mutateAsync(id);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  // -------------------------------------------------------------------------
  // Import / Export handlers
  // -------------------------------------------------------------------------

  async function handleExport() {
    try {
      const res = await api.get(`${basePath}/export`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-contacts-${projectId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Failed to export contacts');
    }
  }

  function handleImport() {
    let parsed: unknown[];
    try {
      parsed = JSON.parse(importJson);
      if (!Array.isArray(parsed)) throw new Error('Not an array');
    } catch {
      toast.error('Invalid JSON. Paste an array of contact objects.');
      return;
    }
    setImportResult(null);
    importContacts.mutate({ contacts: parsed, skipDuplicates });
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/projects/${projectId}/crm`}
          className="hover:bg-muted rounded-lg p-2 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">CRM Settings</h1>
          <p className="text-muted-foreground text-sm">
            Manage pipelines, import and export contacts
          </p>
        </div>
      </div>

      <Tabs defaultValue="pipelines" className="w-full">
        <TabsList>
          <TabsTrigger value="pipelines">
            <Settings className="mr-2 h-4 w-4" />
            Pipelines
          </TabsTrigger>
          <TabsTrigger value="import-export">
            <Upload className="mr-2 h-4 w-4" />
            Import / Export
          </TabsTrigger>
        </TabsList>

        {/* ==================================================================
            Pipelines Tab
        ================================================================== */}
        <TabsContent value="pipelines" className="mt-6 space-y-6">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              {pipelineForms.map((form, pIdx) => {
                const isSaving = savingIds.has(form._id!);
                const isDeleting = deletingIds.has(form._id!);

                return (
                  <Card key={form._id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-lg">
                            {form.isNew ? 'New Pipeline' : form.name}
                          </CardTitle>
                          {form.isDefault && (
                            <Badge variant="secondary" className="text-xs">
                              Default
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      {/* Pipeline name + default toggle */}
                      <div className="flex flex-wrap items-end gap-4">
                        <div className="grid min-w-[240px] flex-1 gap-1.5">
                          <Label>Pipeline Name</Label>
                          <Input
                            value={form.name}
                            onChange={(e) => updateForm(pIdx, { name: e.target.value })}
                          />
                        </div>
                        <label className="flex cursor-pointer items-center gap-2 pb-0.5">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300"
                            checked={form.isDefault}
                            onChange={(e) => updateForm(pIdx, { isDefault: e.target.checked })}
                          />
                          <span className="text-sm font-medium">Default Pipeline</span>
                        </label>
                      </div>

                      <Separator />

                      {/* Stages */}
                      <div className="space-y-3">
                        <Label className="text-sm font-semibold">Stages</Label>
                        {form.stages.map((stage, sIdx) => (
                          <div
                            key={sIdx}
                            className="flex items-center gap-3 rounded-md border px-3 py-2"
                          >
                            <GripVertical className="text-muted-foreground h-4 w-4 flex-shrink-0" />
                            <span className="text-muted-foreground w-6 text-center text-xs font-medium">
                              {sIdx + 1}
                            </span>
                            <div className="relative flex-shrink-0">
                              <input
                                type="color"
                                value={stage.color}
                                onChange={(e) => updateStage(pIdx, sIdx, { color: e.target.value })}
                                className="h-8 w-8 cursor-pointer rounded border-0 p-0"
                                title="Stage color"
                              />
                            </div>
                            <Input
                              className="flex-1"
                              value={stage.name}
                              onChange={(e) => updateStage(pIdx, sIdx, { name: e.target.value })}
                              placeholder="Stage name"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-destructive flex-shrink-0"
                              disabled={form.stages.length <= 2}
                              onClick={() => removeStage(pIdx, sIdx)}
                              title={
                                form.stages.length <= 2
                                  ? 'Minimum 2 stages required'
                                  : 'Remove stage'
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={() => addStage(pIdx)}>
                          <Plus className="mr-2 h-4 w-4" />
                          Add Stage
                        </Button>
                      </div>

                      <Separator />

                      {/* Action buttons */}
                      <div className="flex items-center justify-between">
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={isDeleting}
                          onClick={() => handleDeletePipeline(form, pIdx)}
                        >
                          {isDeleting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="mr-2 h-4 w-4" />
                          )}
                          Delete Pipeline
                        </Button>
                        <Button
                          size="sm"
                          disabled={isSaving}
                          onClick={() => handleSavePipeline(form, pIdx)}
                        >
                          {isSaving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="mr-2 h-4 w-4" />
                          )}
                          {form.isNew ? 'Create Pipeline' : 'Save Pipeline'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              <Button variant="outline" onClick={addNewPipeline}>
                <Plus className="mr-2 h-4 w-4" />
                Create New Pipeline
              </Button>
            </>
          )}
        </TabsContent>

        {/* ==================================================================
            Import / Export Tab
        ================================================================== */}
        <TabsContent value="import-export" className="mt-6 space-y-6">
          {/* Export */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Download className="h-5 w-5" />
                Export Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground text-sm">
                Download all CRM contacts as a JSON file.
              </p>
              <Button variant="outline" onClick={handleExport}>
                <Download className="mr-2 h-4 w-4" />
                Export All Contacts
              </Button>
            </CardContent>
          </Card>

          {/* Import */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Upload className="h-5 w-5" />
                Import Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-muted-foreground space-y-1 text-sm">
                <p>Paste a JSON array of contact objects. Each object should follow this format:</p>
                <pre className="bg-muted mt-2 overflow-x-auto rounded-md p-3 text-xs">
                  {`[
  {
    "email": "jane@example.com",
    "name": "Jane Doe",
    "phone": "+1234567890",
    "company": "Acme Inc",
    "status": "active",
    "tags": ["lead", "enterprise"],
    "score": 80
  }
]`}
                </pre>
                <p className="mt-2">
                  Required: <code className="bg-muted rounded px-1 text-xs">email</code>,{' '}
                  <code className="bg-muted rounded px-1 text-xs">name</code>. Optional:{' '}
                  <code className="bg-muted rounded px-1 text-xs">phone</code>,{' '}
                  <code className="bg-muted rounded px-1 text-xs">company</code>,{' '}
                  <code className="bg-muted rounded px-1 text-xs">status</code>,{' '}
                  <code className="bg-muted rounded px-1 text-xs">tags</code>,{' '}
                  <code className="bg-muted rounded px-1 text-xs">score</code>.
                </p>
              </div>

              <Textarea
                rows={8}
                placeholder='[{ "email": "...", "name": "..." }]'
                value={importJson}
                onChange={(e) => setImportJson(e.target.value)}
              />

              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                />
                <span className="text-sm font-medium">Skip Duplicates</span>
                <span className="text-muted-foreground text-xs">
                  (contacts with the same email will be skipped)
                </span>
              </label>

              <Button
                onClick={handleImport}
                disabled={importContacts.isPending || !importJson.trim()}
              >
                {importContacts.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Import Contacts
              </Button>

              {importResult && (
                <div className="rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    Import complete
                  </p>
                  <ul className="text-muted-foreground mt-1 list-inside list-disc text-sm">
                    <li>
                      <span className="font-medium">{importResult.created}</span> contacts created
                    </li>
                    <li>
                      <span className="font-medium">{importResult.skipped}</span> contacts skipped
                    </li>
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
